/**
 * Focus Management Composable
 * 
 * Advanced focus management utilities for complex accessible components.
 * Handles focus trapping, restoration, and announcement patterns.
 */

import { ref, nextTick, onUnmounted, type Ref } from 'vue';

export interface FocusTrapOptions {
  /** The container element to trap focus within */
  container: Ref<HTMLElement | undefined>;
  /** Whether the trap is currently active */
  active?: Ref<boolean>;
  /** Element to focus when trap is activated */
  initialFocus?: HTMLElement | (() => HTMLElement);
  /** Element to restore focus to when trap is deactivated */
  restoreFocus?: HTMLElement | (() => HTMLElement);
  /** Whether to include the container itself as focusable */
  includeContainer?: boolean;
}

export interface FocusableQuery {
  /** Include disabled elements */
  includeDisabled?: boolean;
  /** Include hidden elements */
  includeHidden?: boolean;
  /** Custom selector to add to the query */
  extraSelectors?: string;
}

/**
 * Enhanced focusable selectors with more comprehensive coverage
 */
const ENHANCED_FOCUSABLE_SELECTORS = [
  // Standard interactive elements
  'a[href]',
  'button',
  'input',
  'select', 
  'textarea',
  
  // Elements with tabindex
  '[tabindex]',
  
  // Contenteditable elements
  '[contenteditable="true"]',
  '[contenteditable=""]',
  
  // Media controls
  'audio[controls]',
  'video[controls]',
  
  // Interactive semantic elements
  'details summary',
  'dialog',
  
  // Custom interactive elements
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="treeitem"]'
];

/**
 * Get focusable elements with enhanced filtering
 */
function getFocusableElements(
  container: HTMLElement,
  options: FocusableQuery = {}
): HTMLElement[] {
  const {
    includeDisabled = false,
    includeHidden = false,
    extraSelectors = ''
  } = options;

  const selectors = [...ENHANCED_FOCUSABLE_SELECTORS];
  if (extraSelectors) {
    selectors.push(extraSelectors);
  }

  const elements = Array.from(
    container.querySelectorAll<HTMLElement>(selectors.join(', '))
  );

  return elements.filter(element => {
    // Check if element is disabled
    if (!includeDisabled && element.hasAttribute('disabled')) {
      return false;
    }

    // Check tabindex
    const tabIndex = element.tabIndex;
    if (tabIndex < 0) return false;

    // Check visibility
    if (!includeHidden && !isElementVisible(element)) {
      return false;
    }

    // Check if element is in an inert container
    if (isInert(element)) return false;

    return true;
  });
}

/**
 * Check if element is visible to screen readers and users
 */
function isElementVisible(element: HTMLElement): boolean {
  // Check if element is physically visible
  if (element.offsetParent === null) return false;
  
  const style = getComputedStyle(element);
  
  // Check CSS visibility properties
  if (style.display === 'none' || 
      style.visibility === 'hidden' || 
      parseFloat(style.opacity) === 0) {
    return false;
  }

  // Check if element is clipped out of view
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  return true;
}

/**
 * Check if element is in an inert container
 */
function isInert(element: HTMLElement): boolean {
  let parent = element.parentElement;
  while (parent) {
    if (parent.hasAttribute('inert') || 
        parent.getAttribute('aria-hidden') === 'true') {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

/**
 * Find the next/previous focusable element in DOM order
 */
function findNextFocusable(
  current: HTMLElement,
  direction: 'next' | 'previous' = 'next'
): HTMLElement | null {
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        const element = node as HTMLElement;
        return getFocusableElements(document.body).includes(element)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      }
    }
  );

  walker.currentNode = current;
  const nextNode = direction === 'next' 
    ? walker.nextNode() 
    : walker.previousNode();
  
  return nextNode as HTMLElement | null;
}

/**
 * Main focus management composable
 */
export function useFocusManagement() {
  const focusStack = ref<HTMLElement[]>([]);
  const activeTraps = ref<Set<HTMLElement>>(new Set());

  /**
   * Save current focus for later restoration
   */
  const saveFocus = (): HTMLElement | null => {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement !== document.body) {
      focusStack.value.push(activeElement);
      return activeElement;
    }
    return null;
  };

  /**
   * Restore previously saved focus
   */
  const restoreFocus = (): boolean => {
    const lastFocused = focusStack.value.pop();
    if (lastFocused && isElementVisible(lastFocused)) {
      try {
        lastFocused.focus();
        return true;
      } catch (error) {
        console.warn('Failed to restore focus:', error);
      }
    }
    return false;
  };

  /**
   * Focus an element with comprehensive error handling
   */
  const focusElement = async (
    target: HTMLElement | (() => HTMLElement),
    options: {
      preventScroll?: boolean;
      selectText?: boolean;
      scrollIntoView?: boolean;
    } = {}
  ): Promise<boolean> => {
    const {
      preventScroll = false,
      selectText = false,
      scrollIntoView = true
    } = options;

    await nextTick();

    const element = typeof target === 'function' ? target() : target;
    if (!element || !isElementVisible(element)) return false;

    try {
      element.focus({ preventScroll });

      if (selectText && element instanceof HTMLInputElement) {
        element.select();
      }

      if (scrollIntoView && !preventScroll) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }

      return document.activeElement === element;
    } catch (error) {
      console.warn('Failed to focus element:', error);
      return false;
    }
  };

  /**
   * Create a focus trap within a container
   */
  const createFocusTrap = (options: FocusTrapOptions) => {
    const {
      container,
      active = ref(false),
      initialFocus,
      restoreFocus: restoreTarget,
      includeContainer = false
    } = options;

    let originalFocus: HTMLElement | null = null;

    const activate = async (): Promise<void> => {
      if (active.value || !container.value) return;

      // Save current focus
      originalFocus = document.activeElement as HTMLElement;
      active.value = true;
      activeTraps.value.add(container.value);

      await nextTick();

      // Set initial focus
      if (initialFocus) {
        const target = typeof initialFocus === 'function' ? initialFocus() : initialFocus;
        await focusElement(target);
      } else {
        // Focus first focusable element or container
        const focusableElements = getFocusableElements(container.value);
        const target = focusableElements[0] || 
                      (includeContainer ? container.value : null);
        
        if (target) {
          await focusElement(target);
        }
      }
    };

    const deactivate = async (): Promise<void> => {
      if (!active.value || !container.value) return;

      active.value = false;
      activeTraps.value.delete(container.value);

      // Restore focus
      const target = restoreTarget
        ? (typeof restoreTarget === 'function' ? restoreTarget() : restoreTarget)
        : originalFocus;

      if (target) {
        await focusElement(target);
      }

      originalFocus = null;
    };

    const handleTabKey = (event: KeyboardEvent): boolean => {
      if (!active.value || event.key !== 'Tab' || !container.value) return false;

      const focusableElements = getFocusableElements(container.value);
      if (focusableElements.length === 0) {
        event.preventDefault();
        return true;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey) {
        // Shift+Tab: move to previous element or wrap to last
        if (activeElement === firstElement) {
          event.preventDefault();
          focusElement(lastElement);
          return true;
        }
      } else {
        // Tab: move to next element or wrap to first
        if (activeElement === lastElement) {
          event.preventDefault();
          focusElement(firstElement);
          return true;
        }
      }

      return false;
    };

    return {
      active,
      activate,
      deactivate,
      handleTabKey
    };
  };

  /**
   * Manage focus within a roving tabindex pattern
   */
  const createRovingTabindex = (
    items: Ref<HTMLElement[]>,
    activeIndex: Ref<number>
  ) => {
    const updateTabindex = (): void => {
      items.value.forEach((item, index) => {
        item.tabIndex = index === activeIndex.value ? 0 : -1;
      });
    };

    const focusItem = async (index: number): Promise<void> => {
      if (index < 0 || index >= items.value.length) return;
      
      activeIndex.value = index;
      updateTabindex();
      await focusElement(items.value[index]);
    };

    return {
      updateTabindex,
      focusItem
    };
  };

  /**
   * Announce text to screen readers
   */
  const announce = (
    message: string,
    priority: 'polite' | 'assertive' = 'polite',
    delay: number = 100
  ): void => {
    // Create temporary live region for announcements
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;

    document.body.appendChild(liveRegion);

    // Add message after a brief delay to ensure it's announced
    setTimeout(() => {
      liveRegion.textContent = message;
      
      // Clean up after announcement
      setTimeout(() => {
        if (document.body.contains(liveRegion)) {
          document.body.removeChild(liveRegion);
        }
      }, 1000);
    }, delay);
  };

  /**
   * Check if focus is trapped
   */
  const isFocusTrapped = (): boolean => {
    return activeTraps.value.size > 0;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    activeTraps.value.clear();
    focusStack.value.length = 0;
  });

  return {
    // State
    focusStack,
    isFocusTrapped,

    // Core methods
    saveFocus,
    restoreFocus,
    focusElement,
    announce,

    // Advanced patterns
    createFocusTrap,
    createRovingTabindex,

    // Utilities
    getFocusableElements,
    isElementVisible,
    findNextFocusable
  };
}

/**
 * Simple focus trap hook for common use cases
 */
export function useFocusTrap(
  container: Ref<HTMLElement | undefined>,
  active: Ref<boolean> = ref(false)
) {
  const focusManagement = useFocusManagement();
  
  return focusManagement.createFocusTrap({
    container,
    active
  });
}

/**
 * Skip link composable for navigation
 */
export function useSkipLink() {
  const skipToContent = (targetId: string = 'main'): void => {
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return {
    skipToContent
  };
}
